import {
  index,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";

/** Marketplace a product fetch targets. Only the key and the dispatch are vendor-aware. */
export enum ProductFetchVendor {
  STOCKX = "STOCKX",
}

/** Lifecycle of one fetch. PARTIAL means some sections landed and the rest did not (yet, or at all). */
export enum ProductFetchStatus {
  PENDING = "PENDING",
  PARTIAL = "PARTIAL",
  COMPLETE = "COMPLETE",
  FAILED = "FAILED",
}

export const productFetchVendorEnum = pgEnum("product_fetch_vendor", [ProductFetchVendor.STOCKX]);

export const productFetchStatusEnum = pgEnum("product_fetch_status", [
  ProductFetchStatus.PENDING,
  ProductFetchStatus.PARTIAL,
  ProductFetchStatus.COMPLETE,
  ProductFetchStatus.FAILED,
]);

/**
 * On-demand product-detail fetch cache. The hourly scrape cron covers breadth;
 * this covers market data for one product a human asked about, so rows are written by
 * the async fetch worker and read by the admin console while it polls.
 *
 * One `payload` column rather than a column per section: which sections a fetch
 * yields is the vendor's business, and StockX's set (market, media, shipping,
 * asks, bids, sales) would be dead columns for the next vendor. The
 * section names live with the vendor's extractor in core/product-fetch.
 *
 * Postgres replaces the boxed spike's DynamoDB table, so staleness is a sweep
 * (PruneProductFetch) rather than a TTL.
 */
export const productFetches = pgTable(
  "product_fetches",
  {
    vendor: productFetchVendorEnum("vendor").notNull(),
    /** Vendor-native product key (the StockX `urlKey` slug): a cache key for products we may not have cataloged, never an FK to external_product_ids. */
    vendorProductKey: text("vendor_product_key").notNull(),
    /** Token of the current fetch run: a later write lands only while it is unchanged. */
    claimId: uuid("claim_id").notNull().defaultRandom(),
    status: productFetchStatusEnum("status").notNull(),
    /** The vendor's own response nodes, keyed by section, pruned of repetition. */
    payload: jsonb("payload"),
    /** A LO user.id, or a `job:`- or `script:`-prefixed name when nobody asked. No FK. */
    requestedBy: text("requested_by").notNull(),
    errorCode: text("error_code"),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ columns: [t.vendor, t.vendorProductKey] }),
    // Serves both the console's `ORDER BY updated_at DESC LIMIT n` and the prune's range delete.
    index("product_fetches_updated_idx").on(t.updatedAt),
    index("product_fetches_status_idx").on(t.status),
  ],
);
